type DateType = 'day' | 'week' | 'month';

/**
 * Subtracts a specified number of days, weeks, or months from a given date.
 * @param dateSource - The original date to modify.
 * @param count - The number of units to subtract.
 * @param type - The type of units to subtract ('day', 'week', or 'month').
 * @returns A new Date object with the modified date.
 */
export const dateSubtract = (dateSource: Date, count: number, type: DateType): Date => {
  const year = dateSource.getFullYear();
  let month = dateSource.getMonth();
  let day = dateSource.getDate();
  const hour = dateSource.getHours();
  const minutes = dateSource.getMinutes();
  const seconds = dateSource.getSeconds();
  const milliseconds = dateSource.getMilliseconds();

  if (type === 'day') {
    day -= count;
  } else if (type === 'week') {
    day -= count * 7;
  } else if (type === 'month') {
    month -= count;
  }

  return new Date(year, month, day, hour, minutes, seconds, milliseconds);
};

/**
 * Formats a date as a string, optionally adapting the format based on the date's proximity to the current date.
 * @param created - The date to format.
 * @param isDateDependsFormat - Whether the format should depend on the proximity to the current date.
 * @returns A formatted date string.
 */
export const getFormattedDate = (created: string | number | Date, isDateDependsFormat = false): string => {
  const date = new Date(created);

  let options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
  };

  if (isDateDependsFormat) {
    const dateNow = new Date();
    const isSameYear = dateNow.getFullYear() === date.getFullYear();
    const isSameDay = isSameYear && dateNow.getMonth() === date.getMonth() && dateNow.getDate() === date.getDate();

    if (isSameDay) {
      options = {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
      };
    } else if (isSameYear) {
      options = {
        month: 'short',
        day: 'numeric',
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
      };
    }
  }

  return date.toLocaleTimeString('en-US', options);
};

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

/**
 * Adds leading zeros to a value to reach the desired length.
 * @param value - The value to pad with leading zeros.
 * @param length - The desired length.
 * @returns A string with the padded value.
 */
export const addZero = (value: number | string, length: number): string =>
  [new Array(length - `${value}`.length).fill(0).join(''), value].join('');

/**
 * Splits a duration in milliseconds into hours, minutes, seconds, and milliseconds.
 * @param ms - The duration in milliseconds.
 * @returns An array with [hours, minutes, seconds, milliseconds].
 */
export const getSplitTime = (ms: number): [number, number, number, number] => {
  const hours = Math.floor(ms / HOUR);
  const minutes = Math.floor((ms - hours * HOUR) / MINUTE);
  const seconds = Math.floor((ms - hours * HOUR - minutes * MINUTE) / SECOND);
  const mSeconds = ms - hours * HOUR - minutes * MINUTE - seconds * SECOND;

  return [hours, minutes, seconds, mSeconds];
};

/**
 * Formats a duration in milliseconds as a video duration string (e.g., '01:30:45').
 * @param ms - The duration in milliseconds.
 * @returns A formatted duration string.
 */
export const getFormattedVideoDuration = (ms: number): string => {
  const [hours, minutes, seconds] = getSplitTime(ms);

  return [hours && addZero(hours, 2), addZero(minutes, 2), addZero(seconds, 2)].filter(Boolean).join(':');
};

/**
 * Formats a duration in milliseconds as a standard duration string (e.g., '01:30:45').
 * @param ms - The duration in milliseconds.
 * @returns A formatted duration string.
 */
export const getFormattedDuration = (ms: number): string => {
  const [hours, minutes, seconds] = getSplitTime(ms);

  return [addZero(hours, 2), addZero(minutes, 2), addZero(seconds, 2)].join(':');
};
